import React, { useEffect, useMemo, useState } from 'react';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { usePersonNameOrder } from '../../hooks/useTableSort';
import {
  getTimeEffective, upsertTimeEffective, bulkUpdateTimeEffective, copyTimeEffectiveYear,
} from '../../api/hrTimeEffective';
import { formatNumber } from '../../utils/format';
import { cmpVal, normalizeName, buildSearchKey } from '../../utils/personTable';
import EditableCell from './EditableCell';
import PersonTh from '../../components/PersonTh';
import PageHeader from '../../components/PageHeader';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import SegmentedToggle from '../../components/SegmentedToggle';
import PersonName from '../../components/PersonName';
import './HrPage.css';

/* Temps effectifs — saisies annuelles par personne.
   Colonnes : Person / Cost pool / FTE % / TT / Holidays / OT / Corp / Supervision /
   Congés spéciaux / TR / Billable / Non bill / Absentéisme (réel) / Idle
   - TT net, OT, TR, Idle : calculés (read-only)
   - Congés spéciaux (carve TO→TR : maternité/paternité/militaire) : saisie manuelle
   - Absentéisme (réel : maladie/imprévu) : lecture seule, Σ des 12 mois du mensuel
     (tdabc_effective_time) — dans le TR, distinct de l'idle
   - Vacances : dérivées du contrat (Vacation days × taux × mois/12), read-only —
     les exceptions individuelles se règlent sur le contrat
   - Toggle h ↔ % du TO pour les catégories d'usage
   - Erreur si Corp+TeamMgt+Bill+NonBill > TO
   - Pools Direction / overhead (corp_is_residual) : Corp = solde automatique,
     Supervision et Idle grisés à 0 ; pools overhead purs : Billable / Non bill
     grisés à 0 (uniquement Corporate + Congés spéciaux). */

const BULK_FIELDS = [
  { key: 'special_leave_hours', label: tp('phrases:p_faf617277d') },
  { key: 'corp_hours',        label: p('Gestion entreprise (heures)') },
  { key: 'team_mgmt_hours',   label: p('Supervision (heures)') },
  { key: 'billable_hours',    label: p('Facturable (heures)') },
  { key: 'nonbillable_hours', label: p('Non facturable (heures)') },
];

export default function TimeEffectivePage({ activeTenant, setPage, addToast, year: yearProp, onYearChange, embedded }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const currentYear = new Date().getFullYear();
  // Année hissée dans App (persistée localStorage). Fallback local.
  const [localYear, setLocalYear] = useState(yearProp ?? currentYear);
  const year = yearProp ?? localYear;
  const setYear = (y) => {
    const next = typeof y === 'function' ? y(year) : y;
    if (onYearChange) onYearChange(next);
    setLocalYear(next);
  };
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol]       = usePersistentValue(`hr_timeeff_sort_col_${tenantId}`, 'last_name_sort');
  const { nameOrder, flipNameOrder } = usePersonNameOrder(`hr_timeeff_name_${tenantId}`, sortCol, setSortCol);
  const [sortDir, setSortDir]       = usePersistentValue(`hr_timeeff_sort_dir_${tenantId}`, 'asc');
  const [filter, setFilter] = usePersistentValue(`hr_timeeff_filter_${tenantId}`, '');
  // « Actif » = statut backend (contrat ouvert à ce jour) ; le backend exclut déjà
  // les personnes sans contrat sur l'année, ce toggle masque en plus les départs.
  const [status, setStatus] = usePersistentValue(`hr_timeeff_status_${tenantId}`, 'all');
  const [showPct, setShowPct]       = usePersistentValue(`hr_timeeff_pct_${tenantId}`, false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  function refresh(silent = false) {
    if (!tenantId) return;
    if (!silent) setLoading(true);
    setError(null);
    getTimeEffective(tenantId, year)
      .then(r => r.error ? setError(r.error) : setDetail(r.data))
      .catch(e => setError(e.message || p('Erreur réseau')))
      .finally(() => { if (!silent) setLoading(false); });
  }
  useEffect(() => refresh(), [tenantId, year]); // eslint-disable-line

  const enrichedRows = useMemo(() => (detail?.rows || []).map(r => ({
    ...r,
    person_name: `${r.last_name} ${r.first_name}`,
    first_name_sort: normalizeName(r.first_name),
    last_name_sort:  normalizeName(r.last_name),
    // fte (backend) = somme des contrats de l'année ; fallback ancien calcul
    // mono-contrat pour rétrocompatibilité.
    fte_pct: r.fte != null
      ? Number(r.fte) * 100
      : Number(r.activity_rate) * Number(r.months_in_year) / 12 * 100,
    // % Idle vs TR — pour tri + affichage colonne dédiée
    idle_pct: Number(r.tr_hours) > 0 ? Number(r.idle_hours) / Number(r.tr_hours) * 100 : 0,
    _search: buildSearchKey(r.first_name, r.last_name, r.cost_pool),
  })), [detail]);

  // Filtre : statut (actifs) + recherche multi-champs (personne, groupe, taux).
  const filteredRows = useMemo(() => {
    const q = normalizeName(filter.trim());
    return enrichedRows.filter(r => {
      if (status === 'active' && !(r.status === 'active' || r.status === 'joined')) return false;
      if (q && !(r._search.includes(q) || `${Math.round(r.fte_pct)}%`.includes(q))) return false;
      return true;
    });
  }, [enrichedRows, filter, status]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const c = cmpVal(a[sortCol], b[sortCol]);
      return sortDir === 'asc' ? c : -c;
    });
    return arr;
  }, [filteredRows, sortCol, sortDir]);

  /* Un total DOIT totaliser les lignes qu'il surmonte. Le pied lisait les
     totaux du moteur (toutes les personnes de l'année) pendant que le corps
     n'affichait que les actifs : 32 pers. annoncées sous 23 lignes, et un TO
     qui ne correspondait à aucun des deux écrans (constat 2026-08-23). Les
     tuiles de synthèse, elles, restent annuelles — c'est la base du CCR, elle
     ne suit pas un filtre d'écran, et son en-tête le dit. */
  const shownTotals = useMemo(() => sortedRows.reduce((a, r) => ({
    fte:                 a.fte + (Number(r.fte) || 0),
    tt_net:              a.tt_net + (Number(r.tt_net) || 0),
    holidays_days:       a.holidays_days + (Number(r.holidays_days) || 0),
    holidays_hours:      a.holidays_hours + (Number(r.holidays_hours) || 0),
    ot_hours:            a.ot_hours + (Number(r.ot_hours) || 0),
    corp_hours:          a.corp_hours + (Number(r.corp_hours) || 0),
    team_mgmt_hours:     a.team_mgmt_hours + (Number(r.team_mgmt_hours) || 0),
    special_leave_hours: a.special_leave_hours + (Number(r.special_leave_hours) || 0),
    tr_hours:            a.tr_hours + (Number(r.tr_hours) || 0),
  }), { fte: 0, tt_net: 0, holidays_days: 0, holidays_hours: 0, ot_hours: 0,
        corp_hours: 0, team_mgmt_hours: 0, special_leave_hours: 0, tr_hours: 0 }),
  [sortedRows]);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  async function patchInput(personId, payload) {
    const r = await upsertTimeEffective(tenantId, year, personId, payload);
    if (r.error) { setError(r.error); return; }
    refresh(true);  // silent : pas de spinner → préserve la scroll position
  }

  async function applyCopyYear(fromY, toY, overwrite) {
    const r = await copyTimeEffectiveYear(tenantId, fromY, toY, overwrite);
    if (r.error) { setError(r.error); return false; }
    setCopyOpen(false);
    setYear(toY);
    refresh();
    if (addToast) addToast(`Copied ${fromY} → ${toY} (${r.data.inserted} new · ${r.data.overwritten} overwritten · ${r.data.skipped} skipped)`);
    return true;
  }

  async function applyBulkUpdate(field, value) {
    const r = await bulkUpdateTimeEffective(tenantId, year, {
      person_ids: [...selected], field, value: String(value),
    });
    if (r.error) { setError(r.error); return false; }
    setBulkOpen(false);
    setSelected(new Set());
    refresh(true);  // silent : préserve la scroll position
    if (addToast) addToast(`Updated ${r.data.updated} rows · ${field} = ${value}`);
    return true;
  }

  // Helpers % toggle
  const pct = (val, base) => (Number(base) > 0 ? (Number(val) / Number(base) * 100) : 0);
  const fmtCell = (val, baseTo) => showPct ? `${formatNumber(pct(val, baseTo), 1)}%` : formatNumber(val, 0);

  // Cellule éditable d'un carve TO→TR. Saisie en heures, OU en % du TO (base
  // stable : TO ne dépend pas des carves) quand le toggle % est actif — la
  // vérité stockée reste en heures. En %, gestion + supervision + congés + TR
  // = 100 % du TO. Fonction (pas composant) → identité stable en cours d'édition.
  const stdEditCell = (hoursVal, ot, fieldKey, patch) => showPct ? (
    <EditableCell
      value={Number(pct(hoursVal, ot).toFixed(1))}
      type="number" align="right" step={0.5}
      formatter={v => (v === '' || v == null ? '' : `${formatNumber(v, 1)}%`)}
      parser={s => { const n = parseFloat(String(s).replace('%', '').replace(',', '.')); return Number.isFinite(n) ? n : 0; }}
      onCommit={v => patch({ [fieldKey]: v == null ? 0 : Math.round((Number(v) / 100) * Number(ot)) })}
    />
  ) : (
    <EditableCell value={hoursVal} type="number" align="right"
                  onCommit={v => patch({ [fieldKey]: v ?? 0 })} />
  );

  // Selection
  const allSelected = sortedRows.length > 0 && sortedRows.every(r => selected.has(r.person_id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleRow = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(prev =>
    prev.size === sortedRows.length ? new Set() : new Set(sortedRows.map(r => r.person_id))
  );

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p("Sélectionner d'abord une entreprise.")}</div></main>;

  const overCount = detail?.totals?.nb_over_tr || 0;

  return (
    /* Embarquée dans un hub, la page ne porte pas son padding haut : le hub
       espace déjà ses onglets, sinon l'écart double (2026-08-30). */
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={t('phrases:p_544722d684')}
          actions={setPage && <button className="hr-page__back" onClick={() => setPage('home')}>{p('← Accueil')}</button>}
        />
      )}

      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {error && (
        <div className="hr-modal__error" style={{ marginBottom: 14 }}>
          {error} <button onClick={() => setError(null)} style={{ marginLeft: 8 }}>×</button>
        </div>
      )}

      {!loading && detail && (
        <>
          {overCount > 0 && (
            <div className="hr-synth__note" style={{ marginBottom: 14 }}>
              ⚠ {overCount} {p('ligne(s) dépassent la capacité : Gestion entreprise + Supervision + Congés spéciaux excèdent le Temps ouvert (TO) → capacité pratique (TR) négative. Réduis ces saisies pour cette (ces) personne(s).')}
            </div>
          )}

          {/* La cascade en tuiles est retirée le 2026-08-30 : la table du
              dessous porte les mêmes colonnes personne par personne, et la
              « Vue d'ensemble » en donne la lecture d'ensemble. */}
          {/* Standard de carte (2026-08-30) : l'unité CHANGE la représentation,
              elle reste dans l'entête, avec les deux actions sur la même ligne.
              Le statut et la recherche RÉDUISENT les lignes : ils passent sous
              l'entonnoir. */}
          <CardShell
            flush
            title={p('Occupation par personne')}
            subtitle={<>{sortedRows.length} {p('ligne(s)')} · {year}
              {filter.trim() !== '' && sortedRows.length !== enrichedRows.length ? ` · ${p('filtrées sur')} ${enrichedRows.length}` : ''}
              {selected.size > 0 ? ` · ${selected.size} ${p('sélectionnée(s)')}` : ''}</>}
            viewControls={(
              <SegmentedToggle
                compact
                label={p('Unité')}
                value={showPct}
                onChange={setShowPct}
                ariaLabel={p('Heures ↔ % du TO')}
                options={[
                  { value: false, label: 'h', title: p('Heures ↔ % du TO') },
                  { value: true, label: '% TO', title: p('Heures ↔ % du TO') },
                ]}
              />
            )}
            primaryAction={(
              <>
                <button className="lk-btn" onClick={() => setBulkOpen(true)} disabled={selected.size === 0}>
                  {p('Mise à jour groupée')}{selected.size > 0 && ` (${selected.size})`}
                </button>
                <button className="lk-btn" onClick={() => { setError(null); setCopyOpen(true); }}
                        title={t('phrases:p_d0c6ec0635')}>{p('Copier une année…')}</button>
              </>
            )}
            filters={(
              <>
                <FilterIcon />
                <SegmentedToggle
                  compact
                  label={p('Statut')} value={status} onChange={setStatus}
                  options={[
                    { value: 'all', label: p('Tous') },
                    { value: 'active', label: p('Actifs'), tone: 'alert' },
                  ]}
                />
                <FilterCount count={(status === 'active' ? 1 : 0) + (filter.trim() ? 1 : 0)}
                             onClear={() => { setStatus('all'); setFilter(''); }} />
              </>
            )}
            search={(
              <SearchField value={filter} onChange={setFilter}
                           placeholder={p('Personne, groupe ou taux…')} />
            )}
            filterCount={(status === 'active' ? 1 : 0) + (filter.trim() ? 1 : 0)}
            collapsible storageKey="temps-standard-occupation"
          >
          <div className="hr-page__table-wrap">
            <table className="hr-page__table hr-page__table--dense">
              <thead>
                <tr>
                  <th className="hr-page__check-col">
                    <input type="checkbox" checked={allSelected}
                           ref={el => { if (el) el.indeterminate = someSelected; }}
                           onChange={toggleAll} />
                  </th>
                  <Th col="person" personSort sortCol={sortCol} sortDir={sortDir} onSort={handleSort}
                      nameOrder={nameOrder} onFlip={flipNameOrder} />
                  <Th col="cost_pool" label={p('Groupe ressources')} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <Th col="fte_pct" label={t('phrases:p_a48842269e')} align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <Th col="tt_net"   label={p('Temps total (TT)')} align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <Th col="holidays_hours"   label={p('Vacances')} align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <Th col="ot_hours" label={t('phrases:p_dcf6e0e541')} align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <Th col="corp_hours"       label={p('Gestion entreprise')} align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <Th col="team_mgmt_hours"  label={p('Supervision production')} align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <Th col="special_leave_hours" label={t('phrases:p_a5fc105a5b')} align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <Th col="tr_hours" label={t('phrases:p_35b83cce91')} align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => {
                  const isSel = selected.has(r.person_id);
                  const patch = (payload) => patchInput(r.person_id, payload);
                  return (
                    <tr key={r.person_id} className={isSel ? 'is-selected' : ''}>
                      <td className="hr-page__check-col">
                        <input type="checkbox" checked={isSel} onChange={() => toggleRow(r.person_id)} />
                      </td>
                      <td><PersonName firstName={r.first_name} lastName={r.last_name} order={nameOrder} /></td>
                      <td className={r.cost_pool ? '' : 'hr-page__muted'}>{r.cost_pool || '—'}</td>
                      <td className="hr-page__num">{formatNumber(r.fte_pct, 1)}%</td>
                      <td className="hr-page__num"><strong>{formatNumber(r.tt_net, 0)}</strong></td>
                      <td className="hr-page__num"
                          title={p('{{days}} j × {{hours}} h — dérivé du contrat (Vacation days × taux × mois/12), se modifie dans Contracts', { days: formatNumber(r.holidays_days, 1), hours: detail.heures_jour })}>
                        {showPct ? `${formatNumber(pct(r.holidays_hours, r.tt_net), 1)}%` : formatNumber(r.holidays_hours, 0)}
                      </td>
                      <td className="hr-page__num"><strong>{formatNumber(r.ot_hours, 0)}</strong></td>
                      <td className={`hr-page__num${r.over_tr ? ' hr-page__cell-warn' : ''}`}>
                        {r.corp_is_residual ? (
                          <span title={p('Solde automatique : TO − Absence − Billable − Non billable (pool overhead pur)')}>
                            {fmtCell(r.corp_hours, r.ot_hours)}
                          </span>
                        ) : (
                          stdEditCell(r.corp_hours, r.ot_hours, 'corp_hours', patch)
                        )}
                      </td>
                      <td className="hr-page__num">
                        {r.corp_is_residual ? (
                          <span className="hr-page__muted" title={p('Pas de supervision — inclus dans Gestion entreprise')}>0</span>
                        ) : (
                          stdEditCell(r.team_mgmt_hours, r.ot_hours, 'team_mgmt_hours', patch)
                        )}
                      </td>
                      <td className="hr-page__num"
                          title={t('phrases:p_661d4b056a')}>
                        {stdEditCell(r.special_leave_hours, r.ot_hours, 'special_leave_hours', patch)}
                      </td>
                      <td className="hr-page__num">
                        <strong>{showPct ? `${formatNumber(pct(r.tr_hours, r.ot_hours), 1)}%` : formatNumber(r.tr_hours, 0)}</strong>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {sortedRows.length > 1 && (
                <tfoot>
                  <tr className="hr-page__total-row">
                    <td className="hr-page__check-col"></td>
                    <td colSpan={2}><strong>Total · {sortedRows.length} pers.</strong></td>
                    <td className="hr-page__num" title="Total FTE (somme des FTE annuels)">
                      <strong>{formatNumber(shownTotals.fte, 2)}</strong>
                    </td>
                    <td className="hr-page__num"><strong>{formatNumber(shownTotals.tt_net, 0)}</strong></td>
                    <td className="hr-page__num" title={`${formatNumber(shownTotals.holidays_days, 1)} j`}>
                      <strong>{formatNumber(shownTotals.holidays_hours, 0)}</strong>
                    </td>
                    <td className="hr-page__num"><strong>{formatNumber(shownTotals.ot_hours, 0)}</strong></td>
                    <td className="hr-page__num"><strong>{formatNumber(shownTotals.corp_hours, 0)}</strong></td>
                    <td className="hr-page__num"><strong>{formatNumber(shownTotals.team_mgmt_hours, 0)}</strong></td>
                    <td className="hr-page__num"><strong>{formatNumber(shownTotals.special_leave_hours, 0)}</strong></td>
                    <td className="hr-page__num"><strong>{formatNumber(shownTotals.tr_hours, 0)}</strong></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          </CardShell>

          {/* Définitions TT / TO / TR sous le tableau */}
          {/* Les définitions TT / TO / TR sont parties au Lexique le
              2026-08-30 : une même explication à deux endroits finit par
              diverger, et l'ampoule est faite pour ça. */}
        </>
      )}

      {bulkOpen && (
        <BulkUpdateModal
          count={selected.size}
          onCancel={() => setBulkOpen(false)}
          onApply={applyBulkUpdate}
        />
      )}
      {copyOpen && (
        <CopyYearModal currentYear={year}
                       onCancel={() => setCopyOpen(false)}
                       onApply={applyCopyYear} />
      )}
    </main>
  );
}


function CopyYearModal({ currentYear, onCancel, onApply }) {
  const { t } = useTranslation();
  const p = useP();
  const [fromY, setFromY] = useState(currentYear);
  const [toY, setToY]     = useState(currentYear + 1);
  const [overwrite, setOverwrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const same = fromY === toY;

  async function submit() {
    if (same || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      const ok = await onApply(fromY, toY, overwrite);
      if (!ok) setErr('Copy failed.');
    } catch (e) { setErr(e.message || p('Erreur réseau')); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">{p('Copier les saisies temps — année à année')}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body">
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Année source')}</label>
            <input className="hr-modal__input" type="number" min="2000" max="2100"
                   value={fromY} onChange={e => setFromY(Number(e.target.value))} />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Année cible')}</label>
            <input className="hr-modal__input" type="number" min="2000" max="2100"
                   value={toY} onChange={e => setToY(Number(e.target.value))} />
          </div>
          <div className="hr-modal__field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
              Écraser les lignes existantes en {toY} (sinon : ignorées par personne)
            </label>
          </div>
          {same && <div className="hr-modal__error">{p('Les années doivent différer.')}</div>}
          {err  && <div className="hr-modal__error">{err}</div>}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary"
                  onClick={submit} disabled={same || submitting}>
            {submitting ? 'Copie…' : `Copier ${fromY} → ${toY}`}
          </button>
        </div>
      </div>
    </div>
  );
}


function Th({ col, label, align, personSort, sortCol, sortDir, onSort, nameOrder, onFlip }) {
  const { t } = useTranslation();
  const p = useP();
  if (personSort) {
    return (
      <PersonTh
        sortCol={sortCol} sortDir={sortDir} onSort={onSort}
        nameOrder={nameOrder} onFlip={onFlip}
        className="hr-page__sort-th hr-page__th-center"
      />
    );
  }
  const isSorted = sortCol === col;
  return (
    <th className={`hr-page__sort-th hr-page__th-center${isSorted ? ' is-sorted' : ''}`}
        onClick={() => onSort(col)}
        title={p('Cliquer pour trier')}>
      <span>{label}</span>
      <span className="hr-page__sort-ind">{isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </th>
  );
}


function BulkUpdateModal({ count, onCancel, onApply }) {
  const { t } = useTranslation();
  const p = useP();
  const [field, setField] = useState('vacances_h');
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    if (submitting) return;
    // Champ vide = 0 (cohérent avec le placeholder « 0 ») → permet la mise à 0 groupée.
    const numVal = value === '' ? 0 : Number(value);
    if (Number.isNaN(numVal)) { setErr(p('La valeur doit être un nombre.')); return; }
    setSubmitting(true);
    setErr(null);
    try { await onApply(field, numVal); }
    catch (e) { setErr(e.message || p('Erreur réseau')); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">{p('Mise à jour groupée')} — {count} {p('ligne(s)')}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body">
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Poste')}</label>
            <select className="hr-modal__input" value={field} onChange={e => setField(e.target.value)}>
              {BULK_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Valeur (heures)')}</label>
            <input className="hr-modal__input" type="number" step="1" min="0"
                   value={value} onChange={e => setValue(e.target.value)} placeholder="0" />
          </div>
          {err && <div className="hr-modal__error">{err}</div>}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary"
                  onClick={submit} disabled={submitting}>
            {submitting ? p('Application…') : `${p('Appliquer à')} ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

